
import { useState, useEffect } from "react";
import { MapPin, Settings, Eye, EyeOff } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { locationService } from "@/services/locationService";

const LocationSettings = () => {
  const { user } = useAuth();
  const [locationEnabled, setLocationEnabled] = useState(false);
  const [precisionMeters, setPrecisionMeters] = useState(300);
  const [showPrecisionToOthers, setShowPrecisionToOthers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasLocation, setHasLocation] = useState(false);

  useEffect(() => {
    if (user) {
      loadLocationSettings();
      checkLocationStatus();
    }
  }, [user]);

  const loadLocationSettings = async () => {
    if (!user) return;

    try {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('location_precision_meters, show_location_precision')
        .eq('user_id', user.id)
        .single();

      if (profile) {
        setPrecisionMeters(profile.location_precision_meters || 300);
        setShowPrecisionToOthers(profile.show_location_precision || false);
      }
    } catch (error) {
      console.error('Error loading location settings:', error);
    }
  };

  const checkLocationStatus = async () => {
    if (!user) return;

    try {
      const location = await locationService.getUserLocation(user.id);
      setHasLocation(!!location);
      setLocationEnabled(!!location);
    } catch (error) {
      console.error('Error checking location status:', error);
    }
  };

  const handleEnableLocation = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const permission = await locationService.checkLocationPermission();
      
      if (permission === 'denied') {
        toast({
          title: "Location Access Denied",
          description: "Please enable location access in your browser settings to use location features.",
          variant: "destructive",
        });
        return;
      }

      const locationData = await locationService.getCurrentLocation();
      await locationService.saveUserLocation(user.id, locationData);
      
      setLocationEnabled(true);
      setHasLocation(true);
      
      toast({
        title: "Location Enabled",
        description: "Your location has been saved successfully.",
      });
    } catch (error) {
      console.error('Error enabling location:', error);
      toast({
        title: "Location Error",
        description: "Failed to enable location. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePrecisionChange = async (newPrecision: string) => {
    if (!user) return;

    const precisionValue = parseInt(newPrecision);
    setPrecisionMeters(precisionValue);

    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ location_precision_meters: precisionValue })
        .eq('user_id', user.id);

      if (error) throw error;

      toast({
        title: "Precision Updated",
        description: "Your location precision setting has been saved.",
      });
    } catch (error) {
      console.error('Error updating precision:', error);
      toast({
        title: "Update Failed",
        description: "Failed to update precision setting.",
        variant: "destructive",
      });
    }
  };

  const handleShowPrecisionToggle = async (enabled: boolean) => {
    if (!user) return;

    setShowPrecisionToOthers(enabled);

    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ show_location_precision: enabled })
        .eq('user_id', user.id);

      if (error) throw error;

      toast({
        title: enabled ? "Precision Now Visible" : "Precision Now Private",
        description: enabled 
          ? "Other users can now see your location precision level." 
          : "Your location precision level is now private.",
      });
    } catch (error) {
      console.error('Error updating precision visibility:', error);
      toast({
        title: "Update Failed",
        description: "Failed to update precision visibility setting.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="bg-gray-800 border-gray-700">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white">
          <MapPin className="w-5 h-5" />
          Location Settings
        </CardTitle>
        <CardDescription>
          Manage your location sharing and precision preferences
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Location Enable/Disable */}
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-white">Enable Location</Label>
            <p className="text-sm text-gray-400">Allow the app to access your location for distance calculations</p>
          </div>
          {!hasLocation ? (
            <Button 
              onClick={handleEnableLocation} 
              disabled={loading}
              size="sm"
            >
              {loading ? "Enabling..." : "Enable"}
            </Button>
          ) : (
            <div className="flex items-center gap-2 text-green-400">
              <MapPin className="w-4 h-4" />
              <span className="text-sm">Enabled</span>
            </div>
          )}
        </div>

        {/* Location Precision */}
        {hasLocation && (
          <div className="space-y-4">
            <div>
              <Label className="text-white">Location Precision</Label>
              <p className="text-sm text-gray-400 mb-3">
                Choose how precise your location appears to others
              </p>
              <Select value={precisionMeters.toString()} onValueChange={handlePrecisionChange}>
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-700 border-gray-600">
                  <SelectItem value="100" className="text-white">Very Precise (±100m)</SelectItem>
                  <SelectItem value="300" className="text-white">Precise (±300m)</SelectItem>
                  <SelectItem value="1000" className="text-white">Approximate (±1km)</SelectItem>
                  <SelectItem value="5000" className="text-white">General Area (±5km)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Show Precision to Others */}
            <div className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg">
              <div className="flex items-center gap-3">
                {showPrecisionToOthers ? (
                  <Eye className="w-5 h-5 text-blue-400" />
                ) : (
                  <EyeOff className="w-5 h-5 text-gray-400" />
                )}
                <div>
                  <Label className="text-white">Show Precision Level</Label>
                  <p className="text-sm text-gray-400">
                    Let others see how precise your location sharing is
                  </p>
                </div>
              </div>
              <Switch
                checked={showPrecisionToOthers}
                onCheckedChange={handleShowPrecisionToggle}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LocationSettings;
